import '../styles/lrpQuotes.scss';

import { CSSProperties, Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { MenuItem, Select, Stack } from '@mui/material';
import { CasButton } from 'src/compass/shared/components';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';

import LrpQuotesTab from './LrpQuotesTab';

// The category color (and optional button color) now come from the lrp.QuoteTabs data,
// applied as CSS custom properties so the SCSS can stay generic.
function getTabColorStyle(tab: any): CSSProperties {
  return {
    ['--tab-color' as any]: tab.baseColor,
    ['--tab-button-color' as any]: tab.buttonColor || tab.baseColor,
  };
}

function getLevelsData(levels: any) {
  return levels.map(({ endDate, subRows }: any) => {
    const [firstSubRow, ...remainingSubRows] = subRows;
    return {
      endDate,
      ...firstSubRow,
      subRows: remainingSubRows.map((remainSubRow: any) => ({ ...remainSubRow, endDate })),
    };
  });
}

function getProcessedQuotesData(data: any[]) {
  return data.reduce((acc, item: any) => {
    const processedItem = {
      ...item,
      types: item.types.map((type: any) => ({
        ...type,
        levels: getLevelsData(type.levels),
      })),
    };
    acc[item.tab] = processedItem;
    return acc;
  }, {});
}

export default function LrpQuotesContent({ data, tabsInfoData, quoteDate }: any) {
  const { mutate } = useAddMutation({
    url: 'LRP/Quotes/UpdateUserSetting',
    onSuccess: () => null,
    showLoader: false,
  });

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [processedData, setProcessedData] = useState<Record<string, any>>({});

  useLayoutEffect(() => {
    if (data) {
      const { result, tabName } = data;
      setActiveTab(tabName);
      setProcessedData(getProcessedQuotesData(result));
    }
  }, [data]);

  useEffect(() => {
    if (activeTab) mutate({ tabName: activeTab });
  }, [activeTab, mutate]);

  const tabList = (tabsInfoData.result ?? [])
    .filter((info: any) => quoteDate >= info.compassEffectiveDate)
    .slice()
    .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
    .map((info: any) => ({
      label: info.tab,
      style: getTabColorStyle(info),
      children: (
        <LrpQuotesTab
          baseColor={info.baseColor}
          buttonColor={info.buttonColor}
          subTitle={info.subTitle}
          tabData={processedData[info.tab]}
        />
      ),
    }));

  const activeTabItem = tabList.find((tab: any) => tab.label === activeTab);

  if (activeTab)
    return (
      <Stack gap={1}>
        <Select
          fullWidth
          value={activeTab}
          onChange={(event) => setActiveTab(event.target.value)}
          className="lrp-quote-select lrp-tab"
          style={activeTabItem?.style}
          renderValue={(label) => <span>{label}</span>}
          MenuProps={{ MenuListProps: { sx: { p: 0 } } }}
        >
          {tabList.map((tab: any) => (
            <MenuItem key={tab.label} value={tab.label} className="lrp-tab-option" style={tab.style}>
              {tab.label}
            </MenuItem>
          ))}
        </Select>
        <Fragment key={activeTab}>{activeTabItem?.children}</Fragment>
      </Stack>
    );

  return (
    <Stack gap={3} alignItems="center">
      {tabList.map((tab: any) => (
        <CasButton
          key={tab.label}
          variant="outlined"
          onClick={() => setActiveTab(tab.label)}
          className="lrp-tab-button"
          style={tab.style}
        >
          {tab.label}
        </CasButton>
      ))}
    </Stack>
  );
}
